// Meta Pixel standard e-commerce events. The Pixel base code (fbq init +
// PageView) lives in index.html; these helpers only fire events on top of it.
// One implementation serves every product — ids, prices and quantities always
// come from the API data passed in. Every helper is a silent no-op when fbq is
// missing (ad blockers, base code not loaded) and never throws into the UI.

const DEFAULT_CURRENCY = 'CAD';
const PURCHASE_STORAGE_KEY = 'itec_meta_purchases';
const PURCHASE_HISTORY_LIMIT = 50;

// Returns true when the event was handed to fbq.
function send(eventName, params, options) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return false;
  try {
    if (options) window.fbq('track', eventName, params, options);
    else window.fbq('track', eventName, params);
    return true;
  } catch {
    // tracking must never break the shopping flow
    return false;
  }
}

const toAmount = (n) => {
  const value = Number(n);
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) / 100 : 0;
};

const toQuantity = (n) => Math.max(1, Math.floor(Number(n)) || 1);

const productIdOf = (p) => p?.id ?? p?.productId ?? p?.product_id;

// Selling price for one unit. Accepts API (snake_case), guest-cart (camelCase)
// and normalized cart (unitPrice/price) shapes; falls back to sale-else-regular.
// The API's `price` is IFNULL(sale_price, regular_price) — what checkout charges.
function unitPriceOf(product) {
  const direct = toAmount(product?.unitPrice) || toAmount(product?.price);
  if (direct > 0) return direct;
  const regular = toAmount(product?.regular_price ?? product?.regularPrice);
  const sale = toAmount(product?.sale_price ?? product?.salePrice);
  return sale > 0 && (regular === 0 || sale < regular) ? sale : regular;
}

// Adds value + currency only when there is a real amount (Meta rejects a
// value without a currency, and a hidden/zero price is not a useful signal).
function withValue(params, value, currency) {
  const amount = toAmount(value);
  if (amount > 0) {
    params.value = amount;
    params.currency = currency || DEFAULT_CURRENCY;
  }
  return params;
}

// Collapses line items to Meta's { id, quantity } contents, dropping rows
// without a product id (e.g. an order line whose product was deleted).
function toContents(items) {
  return (Array.isArray(items) ? items : [])
    .map((item) => ({ id: productIdOf(item), quantity: toQuantity(item?.quantity) }))
    .filter((c) => c.id != null && c.id !== '')
    .map((c) => ({ id: String(c.id), quantity: c.quantity }));
}

export function trackViewContent(product) {
  const id = productIdOf(product);
  if (id == null) return;
  const params = {
    content_ids: [String(id)],
    content_type: 'product',
    content_name: product.name || undefined,
  };
  send('ViewContent', withValue(params, unitPriceOf(product)));
}

export function trackAddToCart(product, quantity = 1) {
  const id = productIdOf(product);
  if (id == null) return;
  const qty = toQuantity(quantity);
  const params = {
    content_ids: [String(id)],
    content_type: 'product',
    content_name: product.name || undefined,
    contents: [{ id: String(id), quantity: qty }],
  };
  send('AddToCart', withValue(params, unitPriceOf(product) * qty));
}

// cart: { items: [{ productId, quantity }], total, currency? }
export function trackInitiateCheckout(cart) {
  const contents = toContents(cart?.items);
  if (!contents.length) return;
  const params = {
    content_ids: contents.map((c) => c.id),
    content_type: 'product',
    contents,
    num_items: contents.reduce((n, c) => n + c.quantity, 0),
  };
  send('InitiateCheckout', withValue(params, cart.total, cart.currency));
}

function readTrackedPurchases() {
  try {
    const list = JSON.parse(localStorage.getItem(PURCHASE_STORAGE_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function rememberPurchase(orderNumber) {
  try {
    const next = [...readTrackedPurchases().filter((n) => n !== orderNumber), orderNumber]
      .slice(-PURCHASE_HISTORY_LIMIT);
    localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // storage unavailable — the eventID below still lets Meta deduplicate
  }
}

// order: { orderNumber, total, currency?, items: [{ productId, quantity }] }
// Call only for a confirmed order. Fires at most once per order in this
// browser (refreshes / revisits of the success page are ignored), and passes
// the order number as eventID so Meta can deduplicate across devices too.
export function trackPurchase(order) {
  const orderNumber = order?.orderNumber != null ? String(order.orderNumber) : '';
  const contents = toContents(order?.items);
  const value = toAmount(order?.total);
  if (!orderNumber || !contents.length || value <= 0) return;
  if (readTrackedPurchases().includes(orderNumber)) return;

  const sent = send(
    'Purchase',
    {
      value,
      currency: order.currency || DEFAULT_CURRENCY,
      content_ids: contents.map((c) => c.id),
      content_type: 'product',
      contents,
      num_items: contents.reduce((n, c) => n + c.quantity, 0),
    },
    { eventID: `purchase_${orderNumber}` }
  );
  if (sent) rememberPurchase(orderNumber);
}
